import { useState } from 'react';
import { VariableSizeList as List } from 'react-window';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import CollapsibleList from 'lib/CollapsibleList';
import { useAvailableHeight } from 'customHooks/useAvailableHeight';
import { SortDir } from 'components/common/types/SortDir.types';
import { useListOfFilesState } from '../useListOfFiles';
import { FileRowContainer } from '../shared/FileRowContainer';
import { renderFilename } from '../shared/renderFilename';
import { ExpandableFilesListProps } from './ExpandableFilesList.types';

const EXTRA_HEIGHT = 20;

const ExpandableFilesList = ({
  uploadedListOfFiles,
  openCategories,
  onToggleCategory,
  renderItem,
  renderHeaderComponent,
}: ExpandableFilesListProps): React.ReactElement => {
  const { currentUploadedListOfFiles, sortDirection, itemHeight, onCategoryDirectionSort } =
    useListOfFilesState(uploadedListOfFiles);

  const [onHoverCategory, setOnHoverCategory] = useState<string>('');

  const { setHeight, getHeight, setRefElement } = useAvailableHeight({
    itemHeight,
    minHeight: itemHeight,
  });

  const handleToggleCategory = (name: string, itemCount: number): void => {
    setHeight(name, itemCount);
    onToggleCategory(name);
  };

  return (
    <>
      {currentUploadedListOfFiles.map((item, index) => {
        if (!item?.fileContents?.length) return null;

        const isOpen = openCategories.includes(item.name);
        const fileItemsCount = item.fileContents.length;
        const availableExpandedHeight = getHeight(item.name);

        return (
          <FileRowContainer
            key={`uploaded-file-container-${item.name}`}
            fileName={item.name}
            onMouseEnter={() => setOnHoverCategory(item.name)}
            onMouseLeave={() => setOnHoverCategory('')}
            setRefElement={(name, element) => setRefElement(name, element as HTMLDivElement)}
          >
            <CollapsibleList
              label={renderFilename(item.name)}
              isSortVisible
              onSort={(direction: SortDir) => onCategoryDirectionSort(item.name, direction)}
              sortDir={sortDirection}
              isOpen={isOpen}
              onToggle={() => handleToggleCategory(item.name, fileItemsCount)}
              headerComponent={renderHeaderComponent(item, index, onHoverCategory === item.name)}
              customExpandedHeight={availableExpandedHeight ? availableExpandedHeight + EXTRA_HEIGHT : undefined}
            >
              {isOpen && fileItemsCount > 0 && (
                <div className="flex w-full overflow-hidden" style={{ height: availableExpandedHeight }}>
                  <AutoSizer>
                    {({ width, height }: Size) => (
                      <List
                        ref={(ref) => ref?.resetAfterIndex(0)}
                        height={height}
                        width={width}
                        itemCount={fileItemsCount}
                        itemSize={() => itemHeight}
                        overscanCount={5}
                      >
                        {(props) => renderItem(item.fileContents![props.index], index, props.style)}
                      </List>
                    )}
                  </AutoSizer>
                </div>
              )}
            </CollapsibleList>
          </FileRowContainer>
        );
      })}
    </>
  );
};

export default ExpandableFilesList;
